import { tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { AuthService } from '../security';

@Injectable({ providedIn: 'root' })
export class PrescriptionService {
    biologicData;
    currentUser;
    constructor(private http: HttpClient, private authService: AuthService) {
        this.initUser();
        this.biologicData = {
            bloodHome: false,
            bloodLab: false,
            NFS: false,
            CRP: false,
            uree: false,
            uricemie: false,
            creatinine: false,
            creatininemie: false,
            ionogramme: false,
            TSH: false,
            T4: false,
            glycemie: false,
            lipidique: false,
            BNP: false,
            NTproBNP: false,
            troponine: false,
            alcalines: false,
            albuminurie: false,
            albuminurie24h: false,
            other: false,
        };
    }

    downloadPrescriptionCurrent(patient, disease) {
        this.downloadPrescription(patient, disease, patient.inclusion.prescriptionFileName);
    }

    downloadPrescription(patient, disease, docId) {
        const filename = this.getPrescriptionFilename(patient, disease, docId);
        this.http
            .get<any>(`${environment.apiUrl}/patient/${patient._id}/prescription/${docId}`, {
                params: { disease },
                responseType: 'blob' as 'json',
            })
            .pipe(tap(data => saveAs(data, filename)))
            .subscribe();
    }

    private getPrescriptionFilename(patient, disease, docId): string {
        const currentInclusion = patient?.inclusion || patient.inclusion;
        const inclusion = [currentInclusion, ...(patient.inclusionHistory || [])].find(patientInclusion => patientInclusion?.prescriptionFileName === docId);

        if (!inclusion?.startDate) {
            return 'ordonnance';
        }

        return `${patient.lastName.toUpperCase()} ${patient.firstName} ${format(new Date(inclusion.startDate), 'ddMMyy')}`;
    }

    deletePrescription(patient, docId) {
        return this.http.delete<any>(`${environment.apiUrl}/patient/${patient._id}/prescription/${docId}`);
    }

    downloadPrescriptionTemplate(id, type, filename = 'prescription.pdf', companyId?) {
        const params = companyId ? { companyId } : undefined;
        return this.http
            .get<any>(`${environment.apiUrl}/user/${id}/prescriptionTemplate/${type}`, { params, responseType: 'blob' as 'json' })
            .pipe(tap(data => saveAs(data, filename)));
    }
    deletePrescriptionTemplate(id, companyId?) {
        const params = companyId ? { companyId } : undefined;
        return this.http.delete<any>(`${environment.apiUrl}/user/${id}/prescriptionTemplate`, { params });
    }
    downloadAuthorization(id, doc, companyId?) {
        const params = companyId ? { companyId } : undefined;
        return this.http
            .get<any>(`${environment.apiUrl}/user/${id}/authorization/${doc._id}`, { params, responseType: 'blob' as 'json' })
            .pipe(tap(data => saveAs(data, doc.filename)));
    }
    deleteAuthorization(id, docId) {
        return this.http.delete<any>(`${environment.apiUrl}/user/${id}/authorization/${docId}`);
    }
    rejectPrescriptionTemplate(id, companyId?) {
        const params = companyId ? { companyId } : undefined;
        return this.http.delete<any>(`${environment.apiUrl}/user/${id}/prescriptionTemplateReject`, { params });
    }

    deletePrescriptionAuthorization(id, companyId?) {
        const params = companyId ? { companyId } : undefined;
        return this.http.delete<any>(`${environment.apiUrl}/user/${id}/prescriptionAuthorization`, { params });
    }
    private initUser(): void {
        this.authService.currentUser
            .pipe(
                tap(user => {
                    this.currentUser = user;
                }),
            )
            .subscribe();
    }
    generatePrescription(patientId, prescriptionDate, initWeight) {
        return this.http.post<any>(`${environment.apiUrl}/patient/${patientId}/prescription`, { prescriptionDate, initWeight }, { responseType: 'blob' as 'json' });
    }
    generatePrescriptionCustom(patientId, prescriptionDate, bodyText) {
        return this.http.post<any>(`${environment.apiUrl}/patient/${patientId}/prescriptionCustom`, { prescriptionDate, bodyText }, { responseType: 'blob' as 'json' });
    }
    getBiologicData() {
        return this.biologicData;
    }
    generatePrescriptionBiologicData(prescriptionDate, patientId, biologicData) {
        return this.http.post<any>(
            `${environment.apiUrl}/patient/${patientId}/prescriptionBiologicData`,
            { prescriptionDate, ...biologicData },
            { responseType: 'blob' as 'json' },
        );
    }

    renewPrescription(patient) {
        return this.http.get<any>(`${environment.apiUrl}/patient/${patient._id}/renewPrescription`);
    }

    uploadPrescription(patient, disease, prescription, inclusionId) {
        const formData: FormData = new FormData();
        formData.append('prescription', prescription, prescription.name);

        return this.http.post<any>(`${environment.apiUrl}/patient/${patient._id}/prescription/${inclusionId}`, formData, { params: { disease } });
    }
}
